import styled from '@emotion/styled';
import SquareButton from '@ep/components/SquareButton';
import { H1 } from '@ep/components/Typography';
import theme from '@ep/theme';
import usePardotForm from '@ep/utils/usePardotForm';
import useQueryParam from '@ep/utils/useQueryParams';
import CircularProgress from '@mui/material/CircularProgress';
import Axios from 'axios';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useState } from 'react';
import {
  CTCard2,
  CTCard3,
  CTCard4,
} from '@ep/components/ContactForm/NewContactForm/CrewAndTalentSupport';
import {
  PSCard2,
  PSCard3,
} from '@ep/components/ContactForm/NewContactForm/ProductSupport';
import {
  SICard2,
  SICard3,
} from '@ep/components/ContactForm/NewContactForm/salesInquiries';

interface FormProps {
  cardState: string;
  setCardState: ({ header, icon, type, card }) => void;
  handleCloseContact: () => void;
  isModal: boolean;
}

const FormContainer = styled.div`
  height: 50%;
  width: 100%;
  ${theme.mediaQueries.mobileAndTablet} {
    height: fit-content;
    max-height: 80rem;
    text-align: center !important;
  }
  button {
    ${theme.mediaQueries.mobileAndTablet} {
      margin-top: 2rem;
    }
  }
`;

const CardContainer = styled.div``;

const ButtonContainer = styled.div`
  text-align: center;
  margin: 0px 10px;
`;

const CardLabel = styled.p`
  font-size: 1.1rem;
  text-align: center;
  margin: 0.25rem;
`;

const StyledSquareButton = styled(SquareButton)`
  border: 1px solid ${theme.colors.mediumGray};
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
  padding: 0.5rem;
  width: 14rem;
  height: 14rem;
  h4 {
    font-size: 1.3rem;
    line-height: 1.4rem;
  }
  .labelBox {
    height: 4.5rem;
  }
`;

const Content = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  align-items: center;
  grid-column: 2;
  grid-row: 2;
  ${theme.mediaQueries.mobile} {
    flex-direction: column;
    grid-row: 1;
    padding-top: 1rem;
    text-align: center !important;
  }
  g {
    clip-path: none;
  }
`;

const FinalContent = styled.div`
  justify-content: space-around;
  text-align: center;
  align-items: center;
  grid-column: 2;
  grid-row: 2;
  ${theme.mediaQueries.mobile} {
    flex-direction: column;
    grid-row: 1;
    padding-top: 1rem;
  }
  p {
    line-height: 2.2rem;
    font-size: 1.6rem;
  }
  a:link {
    font-weight: bold;
  }
`;

const NewContactForm = ({
  cardState,
  setCardState,
  handleCloseContact,
  isModal,
}: FormProps) => {
  const [formData, setFormData] = useState({
    url: '',
    type: '',
    data: {},
  });

  const data = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            contactSalesFormUrl
            contactSupportFormUrl
            contactUsFormUrl
          }
        }
      }
    `,
  );

  const currentUrl =
    typeof window !== 'undefined' ? window.location.pathname : '';
  const currentPath = currentUrl.replace(/\//g, '');

  const { contactSalesFormUrl } = data.site.siteMetadata;
  const { contactSupportFormUrl } = data.site.siteMetadata;
  const { contactUsFormUrl } = data.site.siteMetadata;

  const submit = usePardotForm(contactUsFormUrl);

  const handleSuccess = () => {
    setCardState({
      header: `Thank you`,
      icon: '',
      type: 'success',
      card: 1,
    });
  };

  const handleError = () => {
    setCardState({
      header: `Submission Error`,
      icon: '',
      type: 'error',
      card: 1,
    });
  };

  const handleSalesInquries = () => {
    // dataLayer.push({ event: 'Contact path selected - Sales ' }); 
    //window.location.hash = 'salesCTA';
    //console.log('Charles is Modal', isModal);
    //if (isModal) {
    //  handleCloseContact();
    //}
    setCardState({
      header: `New Products, Services, and Incentives`,
      icon: 'SalesInquiries',
      type: 'SICard',
      card: 2,
    });
    setFormData({
      ...formData,
      url: contactSalesFormUrl,
      type: 'Sales Inquiries',
    });
  };

  const handleCrewAndTalentSupport = () => {
    // dataLayer.push({
    //   event: 'Contact path selected - Crew and Talent Support',
    // });
    setCardState({
      header: `Crew and Talent Support`,
      icon: 'CrewTalentSupport',
      type: 'CTCard',
      card: 2,
    });
    setFormData({
      ...formData,
      url: contactSupportFormUrl,
      type: 'Crew And Talent',
    });
  };

  const handleProductSupport = () => {
    // dataLayer.push({ event: 'Contact path selected - Product Support' }); 
    setCardState({
      header: `Product Support`,
      icon: 'ProductSupportWhite', // should change the icon color or remove it
      type: 'PSCard',
      card: 2,
    });
    setFormData({
      ...formData,
      url: contactSupportFormUrl,
      type: 'Product Support',
    });
  };

  const handleProductSupportQuick = () => {
    // dataLayer.push({ event: 'Contact path selected - Product Support' }); 
    setCardState({
      header: `Product Support`,
      icon: 'ProductSupportWhite',
      type: 'PSCard',
      card: 3,
    });
    setFormData({
      ...formData,
      url: contactSupportFormUrl,
      type: 'Product Support',
    });
  };

  const ContactSelect = () => {
    const formQuick = useQueryParam('form', '')[0];

    if (formQuick === 'product') {
      handleProductSupportQuick();
    }

    if (formQuick === 'sales') {
      handleSalesInquries();
    }

    return (
      <CardContainer>
        <Content>
          <ButtonContainer>
            <StyledSquareButton
              label="Set up a new production"
              icon="EPProductServices"
              //onClick={() => handleSalesInquries()}
              onClick={() => (
                // dataLayer.push({ event: 'Contact path - Sales' }), 
                (window.location.href = '/contact/sales/')
              )}
            />
          </ButtonContainer>
          <ButtonContainer>
            <StyledSquareButton
              label="Crew, Talent, and Background Support"
              icon="CrewTalent"
              onClick={() => handleCrewAndTalentSupport()}
            />
          </ButtonContainer>
          <ButtonContainer>
            <StyledSquareButton
              label="Product Support"
              icon="ProductSupportCard"
              onClick={() => handleProductSupport()}
            />
          </ButtonContainer>
        </Content>
        <CardLabel>
          W-2s, paycheck information, forms,
          <br /> employment verification, insurance
        </CardLabel>
      </CardContainer>
    );
  };

  // const sendToGoogleAnalytics = (formData) => {
  //   currentUrl &&
  //     dataLayer.push({ event: 'Contact click page - ' + currentUrl });
  //   formData.type &&
  //     dataLayer.push({ event: 'Contact path - ' + formData.type });
  //   formData.data.interest &&
  //     dataLayer.push({
  //       event:
  //         'Contact interest - ' +
  //         formData.data.interest +
  //         ' (' +
  //         formData.type.toUpperCase() +
  //         ')',
  //     });
  //   formData.data['00N1Y00000IfGM3'] &&
  //     dataLayer.push({
  //       event: 'Contact location - ' + formData.data['00N1Y00000IfGM3'],
  //     });
  // };

  const onSubmit = (data: { [key: string]: string }) => {
    // Pre set type (path) and form URL if user does not click on a tile
    if (formData.type == '') {
      if (currentPath.includes('incentives') || currentPath.includes('tax')) {
        formData.type = 'Sales Inquiries';
        formData.url = contactSalesFormUrl;
      }
    }
    //
    // sendToGoogleAnalytics(data); 
    const form = document.createElement('form');
    form.method = 'post';
    form.action = formData.url;
    for (const key in formData.data) {
      if (formData.data.hasOwnProperty(key)) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = key;
        hiddenField.value = formData.data[key];
        form.appendChild(hiddenField);
      }
    }
    document.body.appendChild(form);
    form.submit();
  };

  const FormSubmit = () => {
    onSubmit(formData);
    return (
      <CardContainer>
        <Content>
          <CircularProgress size="8rem" />
        </Content>
      </CardContainer>
    );
  };

  const ContactSuccess = () => {
    return (
      <CardContainer>
        <FinalContent>
          <H1>Thank you!</H1>
          <br />
          <p>Please allow 1-2 business days for a reply.</p>
          <br />
          <p>
            If you need product support,{' '}
            <a
              target="_blank"
              href="https://theproductioncommunity.force.com/s/product-support#contact-support"
            >
              click here
            </a>{' '}
            to get the phone number and email address for your designated
            support team.
          </p>
        </FinalContent>
      </CardContainer>
    );
  };

  const ContactError = () => {
    return (
      <CardContainer>
        <FinalContent>
          <H1>Oops!</H1>
          <br />
          <p>The system encountered an error.</p>
          <br />
        </FinalContent>
      </CardContainer>
    );
  };

  const ContactCard = () => {
    switch (cardState) {
      case 'submitting1':
        return <FormSubmit />;
      case 'success1':
        return <ContactSuccess />;
      case 'error1':
        return <ContactError />;
      case '1':
        return <ContactSelect />;
      case 'SICard2':
        return (
          <SICard2
            setCardState={setCardState}
            formState={formData}
            setFormState={setFormData}
          />
        );
      case 'SICard3':
        return (
          <SICard3
            setCardState={setCardState}
            formState={formData}
            setFormState={setFormData}
          />
        );
      case 'CTCard2':
        return (
          <CTCard2
            setCardState={setCardState}
            formState={formData}
            setFormState={setFormData}
          />
        );
      case 'CTCard3':
        return (
          <CTCard3
            setCardState={setCardState}
            formState={formData}
            setFormState={setFormData}
          />
        );
      case 'CTCard4':
        return (
          <CTCard4
            setCardState={setCardState}
            formState={formData}
            setFormState={setFormData}
          />
        );
      case 'PSCard2':
        return (
          <PSCard2
            setCardState={setCardState}
            formState={formData}
            setFormState={setFormData}
          />
        );
      case 'PSCard3':
        return (
          <PSCard3
            setCardState={setCardState}
            formState={formData}
            setFormState={setFormData}
          />
        );
      default:
        return <h1>No project match</h1>;
    }
  };

  return <FormContainer>{ContactCard()}</FormContainer>;
};

export default NewContactForm;
